import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const AdminBoardContext = createContext(null);

const API = process.env.REACT_APP_API_URL;

export function AdminBoardProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [commandCenterData, setCommandCenterData] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Admin authentication
  const adminLogin = async (email, password) => {
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      
      if (!res.ok) throw new Error("Invalid credentials");
      
      const data = await res.json();
      const adminUser = {
        id: data.admin_id || "admin-1",
        email: email,
        name: data.name || "Admin",
        role: data.role || "gsk_super_admin",
        token: data.token
      };
      
      setUser(adminUser);
      localStorage.setItem("speakr_admin", JSON.stringify(adminUser));
      return adminUser;
    } catch (error) {
      throw error;
    }
  };

  const adminLogout = () => {
    setUser(null);
    localStorage.removeItem("speakr_admin");
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  // Load saved admin session
  useEffect(() => {
    const saved = localStorage.getItem("speakr_admin");
    if (saved) {
      try {
        setUser(JSON.parse(saved));
      } catch (e) {
        localStorage.removeItem("speakr_admin");
      }
    }
    setLoading(false);
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = API.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(
      `${wsUrl}/api/admin-board/ws?user_id=${user.id}&user_role=${user.role}`
    );

    ws.onopen = () => {
      console.log("Admin WebSocket connected");
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketEvent(data);
      } catch (e) {
        console.error("WebSocket message parse error:", e);
      }
    };

    ws.onclose = () => {
      console.log("Admin WebSocket disconnected");
      setIsConnected(false);
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    wsRef.current = ws;
  }, [user]);

  // Handle WebSocket events
  const handleWebSocketEvent = (event) => {
    const { type, data } = event;

    switch (type) {
      case "session.started":
        setActiveSessions(prev => [...prev, data]);
        break;
      case "session.ended":
        setActiveSessions(prev => prev.filter(s => s.session_id !== data.session_id));
        break;
      case "session.escalated":
        setAlerts(prev => [{
          id: Date.now(),
          type: "escalation",
          severity: "warning",
          message: `Session escalated: ${data.reason}`,
          session_id: data.session_id,
          timestamp: new Date()
        }, ...prev.slice(0, 49)]);
        break;
      case "approval.requested":
        setPendingApprovals(prev => [data, ...prev]);
        break;
      case "approval.decided":
        setPendingApprovals(prev => prev.filter(a => a.gate_id !== data.gate_id));
        break;
      case "alert":
        setAlerts(prev => [{
          id: Date.now(),
          ...data,
          timestamp: new Date()
        }, ...prev.slice(0, 49)]);
        break;
      case "agent.decision":
      case "human.override":
        // Update relevant session
        break;
      default:
        console.log("Unhandled WebSocket event:", type, data);
    }
  };

  // Connect WebSocket when user is authenticated
  useEffect(() => {
    if (user) {
      connectWebSocket();
    }
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, connectWebSocket]);

  // Fetch Command Center data
  const fetchCommandCenter = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await fetch(
        `${API}/api/admin-board/command-center?admin_id=${user.id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      if (!res.ok) throw new Error("Failed to fetch data");
      
      const data = await res.json();
      setCommandCenterData(data);
      setActiveSessions(data.active_sessions || []);
      setPendingApprovals(data.pending_approvals || []);
    } catch (error) {
      console.error("Command center fetch error:", error);
    }
  }, [user]);

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchCommandCenter();
      // Refresh every 30 seconds
      const interval = setInterval(fetchCommandCenter, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchCommandCenter]);

  // Override actions
  const globalPause = async (reason) => {
    if (!user || user.role !== "gsk_super_admin") {
      throw new Error("Unauthorized: Super Admin only");
    }
    
    const res = await fetch(
      `${API}/api/admin-board/global-pause?admin_id=${user.id}&admin_name=${user.name}&reason=${encodeURIComponent(reason)}`,
      { method: "POST", headers: { Authorization: `Bearer ${user.token}` } }
    );
    
    if (!res.ok) throw new Error("Global pause failed");
    return res.json();
  };

  const globalResume = async () => {
    if (!user || user.role !== "gsk_super_admin") {
      throw new Error("Unauthorized: Super Admin only");
    }
    
    const res = await fetch(
      `${API}/api/admin-board/global-resume?admin_id=${user.id}&admin_name=${user.name}`,
      { method: "POST", headers: { Authorization: `Bearer ${user.token}` } }
    );
    
    if (!res.ok) throw new Error("Global resume failed");
    return res.json();
  };

  const pauseAgent = async (agentId, reason) => {
    const res = await fetch(`${API}/api/admin-board/override`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`
      },
      body: JSON.stringify({
        agent_id: agentId,
        action: "pause",
        reason: reason
      })
    });
    
    if (!res.ok) throw new Error("Pause agent failed");
    return res.json();
  };

  const takeoverCall = async (callControlId) => {
    const res = await fetch(
      `${API}/api/admin-board/takeover/${callControlId}?admin_id=${user.id}&admin_name=${user.name}`,
      { method: "POST", headers: { Authorization: `Bearer ${user.token}` } }
    );
    
    if (!res.ok) throw new Error("Takeover failed");
    return res.json();
  };

  const decideApproval = async (gateId, approved, reason) => {
    const res = await fetch(
      `${API}/api/admin-board/approvals/decide?admin_id=${user.id}&admin_name=${user.name}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ gate_id: gateId, approved, reason })
      }
    );
    
    if (!res.ok) throw new Error("Approval decision failed");
    await fetchCommandCenter();
    return res.json();
  };

  const enableSafeMode = async (businessId, reason, config = {}) => {
    const res = await fetch(
      `${API}/api/admin-board/safe-mode/enable?admin_id=${user.id}&admin_name=${user.name}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ business_id: businessId, reason, ...config })
      }
    );
    
    if (!res.ok) throw new Error("Enable safe mode failed");
    return res.json();
  };

  const value = {
    user,
    loading,
    isConnected,
    commandCenterData,
    activeSessions,
    pendingApprovals,
    alerts,
    adminLogin,
    adminLogout,
    fetchCommandCenter,
    globalPause,
    globalResume,
    pauseAgent,
    takeoverCall,
    decideApproval,
    enableSafeMode
  };

  return (
    <AdminBoardContext.Provider value={value}>
      {children}
    </AdminBoardContext.Provider>
  );
}

export function useAdminBoard() {
  const context = useContext(AdminBoardContext);
  if (!context) {
    throw new Error("useAdminBoard must be used within AdminBoardProvider");
  }
  return context;
}
